import type { NoteLine, PartLine, RawNote, RawRest, Timeline, TimelineNote, VoicePart } from './model';

const EPS = 1e-6;

const voiceKey = (x: { staff: number; voice: string }) => `${x.staff}|${x.voice}`;

/**
 * Decide which line of a divided part sings each note (divisi), measure by measure:
 *
 * - A note that overlaps another note of the part is on the upper line (1) when the other note is
 *   below it, the lower line (2) when it is above it, and an inner note (3) when it has notes both
 *   above and below (a three-way division).
 * - "Above" means a higher pitch inside one voice (a chord), and the higher voice between voices
 *   (voices are ranked by their average pitch in the part), so crossing voices keep their lines.
 * - A visible rest in another voice counts like a note there: that line is silent, so the note is
 *   only for this line. A hidden rest, or no rest at all, does not.
 * - A note that overlaps nothing is sung by every line (0): the part is not divided there.
 *
 * Changes the notes in place.
 */
export function assignLines(notes: RawNote[], rests: readonly RawRest[]): void {
  // Rank voices by average pitch, highest first; voice numbers break ties.
  const sums = new Map<string, { sum: number; count: number; voice: number }>();
  for (const n of notes) {
    const key = voiceKey(n);
    const s = sums.get(key) ?? { sum: 0, count: 0, voice: Number.parseInt(n.voice, 10) || 0 };
    s.sum += n.midi;
    s.count++;
    sums.set(key, s);
  }
  const rank = new Map<string, number>();
  [...sums.entries()]
    .sort(([, a], [, b]) => b.sum / b.count - a.sum / a.count || a.voice - b.voice)
    .forEach(([key], i) => rank.set(key, i));

  const byMeasure = new Map<number, { notes: RawNote[]; rests: RawRest[] }>();
  const bucket = (m: number) => {
    let b = byMeasure.get(m);
    if (!b) {
      b = { notes: [], rests: [] };
      byMeasure.set(m, b);
    }
    return b;
  };
  for (const n of notes) bucket(n.measureIndex).notes.push(n);
  for (const r of rests) if (r.visible) bucket(r.measureIndex).rests.push(r);

  for (const { notes: ns, rests: rs } of byMeasure.values()) {
    // Only rests of voices that sing in this measure: a voice that never sings here says nothing.
    const singing = new Set(ns.map(voiceKey));
    for (const n of ns) {
      const end = n.offset + n.duration;
      const overlaps = (x: { offset: number; duration: number }) => x.offset < end - EPS && x.offset + x.duration > n.offset + EPS;
      const own = rank.get(voiceKey(n)) ?? 0;
      let above = false;
      let below = false;
      for (const m of ns) {
        if (m === n || !overlaps(m)) continue;
        const sameVoice = voiceKey(m) === voiceKey(n);
        if (sameVoice) {
          if (m.midi > n.midi) above = true;
          else if (m.midi < n.midi) below = true;
        } else if ((rank.get(voiceKey(m)) ?? 0) < own) above = true;
        else below = true;
      }
      for (const r of rs) {
        if (voiceKey(r) === voiceKey(n) || !singing.has(voiceKey(r)) || !overlaps(r)) continue;
        if ((rank.get(voiceKey(r)) ?? 0) < own) above = true;
        else below = true;
      }
      n.line = above && below ? 3 : above ? 2 : below ? 1 : 0;
    }
  }
}

/** True if a note is sung on this line of its part. */
export function onLine(note: { line: NoteLine }, line: PartLine): boolean {
  return note.line === 0 || note.line === line;
}

/** How many lines a voice part has in this timeline: 2 where it divides somewhere, otherwise 1. */
export function partLineCount(timeline: Timeline, voicePart: VoicePart): 1 | 2 {
  return timeline.notes.some((n) => n.voicePart === voicePart && (n.line === 1 || n.line === 2)) ? 2 : 1;
}

/** The notes of a voice part's other line: where it divides, the notes the chosen line does not sing. */
export function isOtherLine(note: TimelineNote, voicePart: VoicePart, line: PartLine): boolean {
  return note.voicePart === voicePart && !onLine(note, line);
}
